/**
 * Vite SPA entry point.
 *
 * Mounts the route table: the workspace at ``/`` (gated behind
 * AuthGate), plus the public auth surface at ``/login``,
 * ``/register``, ``/mfa/*``, and ``/reset/*``. The auth-context
 * provider wraps everything so each route can call ``useAuth``.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { App } from "./App";
import { AuthProvider } from "./auth/AuthContext";
import { AuthGate } from "./auth/AuthGate";
import { RoleGate } from "./auth/RoleGate";
import { BrandingProvider } from "./branding/BrandingProvider";
import { Login } from "./views/auth/Login";
import { Register } from "./views/auth/Register";
import { MfaChallenge } from "./views/auth/MfaChallenge";
import { MfaEnrol } from "./views/auth/MfaEnrol";
import { PasswordResetRequest } from "./views/auth/PasswordResetRequest";
import { PasswordResetConfirm } from "./views/auth/PasswordResetConfirm";
import { AdminShell } from "./views/admin/AdminShell";
import { TenantsPage } from "./views/admin/Tenants";
import { PlatformBrandingPage } from "./views/admin/Branding";
import { CustomDomainsPage } from "./views/admin/CustomDomains";
import { AuditPage } from "./views/admin/Audit";
import { LlmConfigPage } from "./views/admin/LlmConfig";
import { LlmDashboardPage } from "./views/admin/LlmDashboard";
import { LlmRatesPage } from "./views/admin/LlmRates";
import { MembersPage } from "./views/admin/Members";
import "./styles.css";

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element missing; index.html is the source of truth for the mount point.");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <BrandingProvider>
        <AuthProvider>
          <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/mfa/challenge" element={<MfaChallenge />} />
          <Route path="/mfa/enrol" element={<AuthGate><MfaEnrol /></AuthGate>} />
          <Route path="/reset/request" element={<PasswordResetRequest />} />
          <Route path="/reset" element={<PasswordResetConfirm />} />
            <Route
              path="/admin/platform"
              element={
                <AuthGate>
                  <RoleGate allow={["platform_owner"]}>
                    <AdminShell />
                  </RoleGate>
                </AuthGate>
              }
            >
              <Route index element={<TenantsPage />} />
              <Route path="tenants" element={<TenantsPage />} />
              <Route path="branding" element={<PlatformBrandingPage />} />
              <Route path="custom-domains" element={<CustomDomainsPage />} />
              <Route path="audit" element={<AuditPage />} />
              <Route path="members" element={<MembersPage />} />
              <Route path="llm/config" element={<LlmConfigPage />} />
              <Route path="llm/rates" element={<LlmRatesPage />} />
              <Route path="llm/dashboard" element={<LlmDashboardPage />} />
            </Route>
            <Route
              path="*"
              element={
                <AuthGate>
                  <App />
                </AuthGate>
              }
            />
          </Routes>
        </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
